import Styles from "./style.module.scss";
import React, { useState } from "react";
import { PhotoPostPage, PropsPhoto } from "../../../../types";
import { handlerDataImageForModal, handlerModalWithImage } from "../../../../redux/Reducers/photoSlice";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import classNames from "classnames/bind";
import { motion } from "framer-motion";
import { getPhotoAlt } from "../../../../utils/photoAccessibility";
import { updatePhotoAlt } from "../../../../redux/Reducers/photoSlice";

const Photo: React.FC<PropsPhoto> = ({ image, imageIndex, photoPostPage, handleDeletePhoto, showPhotos, variants }) => {
  const { user } = useAppSelector((state) => state.user);
  const { openChangeSortPhotos } = useAppSelector((state) => state.photos);
  const dispatch = useAppDispatch();
  const [imageFailed, setImageFailed] = useState(false);
  const [showAltEditor, setShowAltEditor] = useState(false);
  const [altDraft, setAltDraft] = useState(image.alt || "");
  const [altError, setAltError] = useState("");
  const [savingAlt, setSavingAlt] = useState(false);

  const handleOpenImagePopup = (linkPhoto: string): void => {
    dispatch(handlerModalWithImage(true));
    dispatch(handlerDataImageForModal(linkPhoto));
  };

  const cx = classNames.bind(Styles);

  const styleContainerPhoto = cx(
    "photo",
    { photo__containerGallery: photoPostPage === PhotoPostPage.photoGalleryPage },
    { photo__container: photoPostPage === PhotoPostPage.mainPage }
  );

  const imageAlt = getPhotoAlt(image, imageIndex);
  const shouldLoadEagerly = photoPostPage === PhotoPostPage.photoGalleryPage && imageIndex < 4;

  const handleSaveAlt = async () => {
    const description = altDraft.trim();
    if (description.length < 5 || description.length > 180) {
      setAltError("Use between 5 and 180 characters.");
      return;
    }
    setSavingAlt(true);
    setAltError("");
    try {
      await dispatch(updatePhotoAlt({ photoId: image._id, alt: description })).unwrap();
      setShowAltEditor(false);
    } catch (error: any) {
      setAltError(typeof error === "string" ? error : "The photo description could not be saved.");
    } finally {
      setSavingAlt(false);
    }
  };

  return (
    <motion.li className={styleContainerPhoto} variants={variants}>
      {user.role.includes("ADMIN") && (
        <>
          {openChangeSortPhotos && <div className={Styles.photo__containerBtnUpDown} />}
          <button
            type="button"
            className={Styles.photo__editAlt}
            aria-label="Edit photo description"
            onClick={() => {
              setAltDraft(image.alt || "");
              setAltError("");
              setShowAltEditor(true);
            }}
          >
            ALT
          </button>
          <button
            type="button"
            aria-label="Delete photograph"
            className={Styles.photo__delete}
            onClick={() => handleDeletePhoto(image._id, showPhotos)}
          />
        </>
      )}
      {user.role.includes("ADMIN") && showAltEditor && (
        <div className={Styles.photo__altEditor} role="dialog" aria-label="Edit photo description">
          <label htmlFor={`photo-alt-${image._id}`}>Describe what is visible</label>
          <textarea
            id={`photo-alt-${image._id}`}
            value={altDraft}
            maxLength={180}
            onChange={(event) => setAltDraft(event.target.value)}
          />
          <small>{altDraft.trim().length}/180 characters</small>
          {altError && <p role="alert">{altError}</p>}
          <div>
            <button type="button" disabled={savingAlt} onClick={handleSaveAlt}>
              {savingAlt ? "Saving…" : "Save"}
            </button>
            <button type="button" disabled={savingAlt} onClick={() => setShowAltEditor(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {imageFailed ? (
        <div className={Styles.photo__imageError} role="img" aria-label={`${imageAlt} is temporarily unavailable`}>
          Photo unavailable
        </div>
      ) : (
        <>
          <img
            className={Styles.photo__image}
            src={image.image}
            width="300"
            height="300"
            loading={shouldLoadEagerly ? "eager" : "lazy"}
            decoding="async"
            alt={imageAlt}
            onError={() => setImageFailed(true)}
          />
          <button
            className={Styles.photo__overlay}
            type="button"
            aria-label={`Open ${imageAlt}`}
            onClick={() => handleOpenImagePopup(image.image)}
          >
            <div className={Styles.photo__overlayIcon} />
          </button>
        </>
      )}
    </motion.li>
  );
};

export default Photo;
